import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { BASE_URL, session as hubSession, useHubLogin } from '../../session';
import { createAuthClient, errorFromResponse, type AuthClient } from '../../identity/api';
import { hubFetch, proxyFetch, targetSession } from '../../identity/session';

/**
 * M12 transport: nyce.buddy's admin API through the hub proxy `/buddy-admin/<target>/<path>` (buddy3's
 * `/api/buddy/admin/<path>`; `feedback…` is its chat triage, `translations/languages` its chat route). The environment's
 * AUTH session (the one the identity pages use) travels as `X-Target-Token`; buddy3 accepts it when the operator tenant
 * holds `nycebuddy:admin` there. Paths start with `/`; only routes in the hub's allow-list (`Buddy/BuddyEndpoints.cs`)
 * get through. Area modules (`usage.ts`, `quality.ts`, `setup.ts`, …) build on `buddyClient`.
 */

export type BuddyMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** `{a: 1, b: undefined, c: false}` → `?a=1`; empty values and `false` are left out. */
export const qs = (q: Record<string, string | number | boolean | null | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(q))
    if (v !== undefined && v !== null && v !== '' && v !== false) s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : '';
};

const base = (target: string) => `${BASE_URL}/buddy-admin/${encodeURIComponent(target)}`;
const clients = new Map<string, AuthClient>();

/** Raw call with the target token (one refresh on a target 401): downloads and multipart uploads. */
async function raw(target: string, method: BuddyMethod, path: string, body?: BodyInit): Promise<Response> {
  const session = targetSession(target);
  const send = async (token: string | null) => {
    const h = new Headers();
    if (token) h.set('X-Target-Token', token);
    return hubFetch(`${base(target)}${path}`, { method, headers: h, body });
  };
  let r = await send(await session.getToken());
  if (r.status === 401 && body === undefined) {
    const fresh = await session.refresh().catch(() => null);
    if (fresh) r = await send(fresh);
  }
  if (!r.ok) throw await errorFromResponse(r);
  return r;
}

/**
 * Multipart POST with upload progress (fetch cannot report bytes sent): XHR with the hub token and the target token.
 * Errors carry the hub's `{error}` message like the JSON calls.
 */
async function xhrUpload<T>(
  target: string,
  path: string,
  form: FormData,
  onProgress: (sent: number, total: number) => void
): Promise<T> {
  const [hub, token] = await Promise.all([hubSession.getToken(), targetSession(target).getToken()]);
  return new Promise<T>((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('POST', `${base(target)}${path}`);
    if (hub) x.setRequestHeader('Authorization', `Bearer ${hub}`);
    if (token) x.setRequestHeader('X-Target-Token', token);
    x.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    x.onerror = () => reject(new Error('upload failed: network error'));
    x.onload = () => {
      let body: unknown = null;
      try {
        body = x.responseText ? JSON.parse(x.responseText) : null;
      } catch {
        /* not JSON */
      }
      if (x.status >= 200 && x.status < 300) resolve(body as T);
      else
        reject(new Error((body as { error?: string } | null)?.error ?? `upload failed (HTTP ${x.status})`));
    };
    x.send(form);
  });
}

/** One environment's nyce.buddy: JSON `get`/`send`, `download` (blob + file name) and multipart `upload`. */
export function buddyClient(target: string) {
  let c = clients.get(target);
  if (!c) {
    c = createAuthClient({ baseUrl: base(target), auth: targetSession(target), fetch: proxyFetch });
    clients.set(target, c);
  }
  const json = c;
  return {
    get: <T>(path: string) => json.request<T>('GET', path),
    send: <T>(method: Exclude<BuddyMethod, 'GET'>, path: string, body?: unknown) =>
      json.request<T>(method, path, body),
    /** A file response (provisioning SQL, translation export, customer document): its blob and suggested name. */
    download: async (path: string, fallbackName: string): Promise<{ blob: Blob; fileName: string }> => {
      const r = await raw(target, 'GET', path);
      const cd = r.headers.get('Content-Disposition') ?? '';
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
      return { blob: await r.blob(), fileName: m ? decodeURIComponent(m[1] ?? m[2]) : fallbackName };
    },
    /** Multipart upload (customer documents); the browser sets the boundary. `onProgress` = bytes sent so far. */
    upload: async <T>(
      path: string,
      form: FormData,
      onProgress?: (sent: number, total: number) => void
    ): Promise<T> =>
      onProgress
        ? xhrUpload<T>(target, path, form, onProgress)
        : ((await raw(target, 'POST', path, form)).json() as Promise<T>),
  };
}

/** Save a blob as a file (downloads). */
export function saveBlob(blob: Blob, fileName: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** Whether the hub lets this operator change nyce.buddy (`ophub:buddy:admin`); buddy3 still checks its own scope. */
export function useBuddyWrite(): boolean {
  return hasScope(useHubLogin()?.scopes, SCOPES.buddyAdmin);
}

/** Test hook. */
export const resetBuddyClients = () => clients.clear();
